// public/js/features/tracker-config/battery-remaining-ui.js
// The remaining-battery pill shown on a device (asset detail card, devices
// list, device modal). Reads the estimate off the raw device row via
// battery-usage.js - the same module the Lambda books with - so the number on
// screen is exactly what the accounting says, with no second implementation.
//
// Tooltips go through the app's standard help mechanism (ui/help.js:
// data-help + initHelp -> Bootstrap tooltip, customClass app-help), not the
// browser's native title. The copy is per device, so the dictionary is built
// on the fly from the rows in view - wireBatteryHelp() does that.
//
// Feature-flagged: an environment without FEATURE_BATTERY_ESTIMATE renders
// nothing here, because its device rows carry no accounting to read.
import { estimateRemaining, formatRemaining } from './battery-usage.js';
import { featureEnabled } from '../../modules/features.js';
import { initHelp } from '../../ui/help.js';
import { escapeHtml } from '../../utils/escape.js';

const toneFor = (est) => {
  if (est.method === 'externalPower') return 'gray';
  const d = est.estRemainingDays;
  if (!isFinite(d)) return 'gray';
  if (d < 60) return 'red';
  if (d < 180) return 'amber';
  return 'green';
};

/** Tooltip key for a device - one entry per device in the on-the-fly dictionary. */
const helpKey = (device) => `battery.${device.deviceId}`;

/** The derivation as help HTML (initHelp renders html: true). */
export function batteryHelpHtml(est) {
  const e = escapeHtml;
  const lines = [
    `<strong>Estimated remaining battery</strong> <span class="text-muted">model v${est.modelVersion}, confidence ${e(est.confidence)}</span>`,
    `${e(est.family)}: ${est.consumedMAh.toFixed(0)} of ${est.usableMAh.toFixed(0)} mAh used (${est.consumedPct.toFixed(1)} %)`,
    `Rate ${est.ratePerDay.toFixed(2)} mAh/day from the ${est.rateSource === 'window30' ? 'last 30 days' : 'lifetime average'}`,
    `Counting since ${e(String(est.epochAt).slice(0, 10))} (${est.epochKind === 'firstContact' ? 'first contact' : e(est.epochKind)}), ${est.uplinks} uplinks`,
    ...est.notes.map(n => `<span class="text-muted">${e(n)}</span>`),
  ];
  return lines.join('<br>');
}

/**
 * HTML for the pill, or '' when there is nothing to show. Soft-pill utilities
 * per the project convention. Carries data-help; call wireBatteryHelp() once
 * the HTML is in the DOM.
 */
export function batteryRemainingPill(device) {
  if (!featureEnabled('batteryEstimate')) return '';
  const est = estimateRemaining(device);
  if (!est) return '';
  const label = formatRemaining(est);
  const tone = toneFor(est);
  const conf = est.confidence === 'low' ? ' <i class="fa-regular fa-circle-question ms-1" style="opacity:.7"></i>' : '';
  return `<span class="badge badge-soft-${tone} battery-remaining" data-help="${escapeHtml(helpKey(device))}" tabindex="0">`
    + `<i class="fa-solid fa-battery-half me-1"></i>${escapeHtml(label)}${conf}</span>`;
}

/** Help dictionary for every device in view that has an estimate. */
export function batteryHelpDict(devices = []) {
  const dict = {};
  if (!featureEnabled('batteryEstimate')) return dict;
  for (const d of devices) {
    const est = d ? estimateRemaining(d) : null;
    if (est) dict[helpKey(d)] = batteryHelpHtml(est);
  }
  return dict;
}

/** Wire the standard tooltips for every battery pill inside `container`. */
export function wireBatteryHelp(container, devices = []) {
  if (!container) return;
  initHelp(container, batteryHelpDict(devices));
}

/**
 * Sort key for a table column: remaining days ascending, so the device that
 * needs attention first sorts first. External power and "10+ years" sort last
 * among the estimated; a device with no estimate returns null and the table
 * sorter puts it after everything else.
 */
export function batterySortValue(device) {
  if (!featureEnabled('batteryEstimate')) return null;
  const est = estimateRemaining(device);
  if (!est) return null;
  return isFinite(est.estRemainingDays) ? est.estRemainingDays : 1e9;
}
